import { ref, computed } from 'vue';
import moment from 'moment';

export function useChartInteractions(cycleChartData, allSupplySerialCycles, selectedSupplySerial, partnerView, showCycle, showFwView, timezone, timezoneFormat, hubTzOffset) {
  // Convert non-ref parameters to refs for reactivity
  const cycleChartDataRef = ref(cycleChartData);
  const allSupplySerialCyclesRef = ref(allSupplySerialCycles);
  const selectedSupplySerialRef = ref(selectedSupplySerial);
  const partnerViewRef = ref(partnerView);
  const showCycleRef = ref(showCycle);
  const showFwViewRef = ref(showFwView);

  const selectedCycleDetails = ref(null);
  const cycleHoverState = ref({
    isHovered: false,
    cycleIndex: null,
    cycleData: null
  });
  const selectedCycleIndex = ref(null);
  const hoveredCycleIndex = ref(null);

  // Computed properties for chart data
  const datetime = computed(() => {
    return cycleChartDataRef.value?.datetime?.map(d => formatTimezone(d, partnerViewRef.value, timezone.value, timezoneFormat.value, hubTzOffset.value)) || [];
  });

  const returnTemp = computed(() => {
    return cycleChartDataRef.value?.filterTemp || [];
  });

  const supplyTemp = computed(() => {
    return cycleChartDataRef.value?.comfortTemp || [];
  });

  const atticTemp = computed(() => {
    return cycleChartDataRef.value?.atticTemp || [];
  });

  const outsideTemp = computed(() => {
    return cycleChartDataRef.value?.outsideTemp || [];
  });

  const humidity = computed(() => {
    return cycleChartDataRef.value?.humidity || [];
  });

  const filterPressure = computed(() => {
    return cycleChartDataRef.value?.filterPressure || [];
  });

  const dataset = computed(() => {
    return cycleChartDataRef.value?.firmwareDataset || {};
  });

  const cycleData = computed(() => {
    return cycleChartDataRef.value?.cycleData || [];
  });

  const actualCycleData = computed(() => {
    if (allSupplySerialCyclesRef.value && selectedSupplySerialRef.value) {
      return allSupplySerialCyclesRef.value[selectedSupplySerialRef.value] || [];
    }
    return [];
  });

  // Helper function for timezone formatting (using mixin's logic)
  function formatTimezone(dateString, partnerView, timezone, timezoneFormat, hubTzOffset) {
    if (!dateString) return '';
    let value = '-';

    if (dateString) {
      let hourOption = 'h:mm:ss A';
      if (partnerView) {
        return moment(dateString).format(`ddd, MM-DD-YYYY, ${hourOption}`);
      }
      if (timezoneFormat === 24) {
        hourOption = 'H:mm:ss';
      }
      if (timezone === 'utc') {
        value = moment.utc(dateString).format(`ddd, MM-DD-YYYY, ${hourOption}`);
      } else if (timezone === 'local') {
        value = moment(dateString).format(`ddd, MM-DD-YYYY, ${hourOption}`);
      } else if (timezone === 'member' && hubTzOffset) {
        value = moment(dateString).utcOffset(hubTzOffset).format(`ddd, MM-DD-YYYY, ${hourOption}`);
      }
    }
    return value;
  }

  // Chart series configuration
  const dataSeries = computed(() => {
    if (showFwViewRef.value) {
      return Object.entries(dataset.value).map(([key, value]) => ({
        name: key,
        color: getRandomBrightColor(),
        data: value || [],
        yAxis: key.includes('-pressure') ? 1 : 0,
      }));
    }

    const series = [
      { name: 'Supply T', color: '#FFCD66', data: supplyTemp.value || [] },
      { name: 'Return T', color: '#F83356', data: returnTemp.value || [] },
      { name: 'Attic Temp', color: '#2759E4', data: atticTemp.value || [] },
      { name: 'Humidity', color: '#2759E4', data: humidity.value || [] },
      { name: 'Pressure', color: '#66D383', data: filterPressure.value || [], yAxis: 1 },
    ];

    series.push({
      name: 'Outside Temp',
      color: 'var(--text-color)',
      data: outsideTemp.value || [],
    });

    return series;
  });

  // Chart options configuration
  const chartOptions = computed(() => {
    return {
      title: '',
      chart: {
        type: 'line',
        zoomType: 'x',
        height: 500,
        spacingTop: 25,
        spacingBottom: 50,
        spacingRight: 25,
        backgroundColor: 'var(--chart-bg)',
        style: {
          fontFamily: 'Poppins, sans-serif',
        },
        events: {
          load() {
            const chart = this;
            setupChartEventHandlers(chart);
          },
          mouseMove(e) {
            // Store the last mouse position and hovered points for copy functionality
            const chart = this;
            const componentInstance = chart.vueComponent || window.cycleChartComponent;
            if (componentInstance) {
              componentInstance.lastMouseEvent = e;
              componentInstance.lastHoveredPoints = chart.hoverPoints;
            }
          },
        },
      },
      tooltip: {
        useHTML: true,
        nullFormat: 'Value is not available.',
        shared: true,
        formatter() {
          return formatTooltip(this);
        },
      },
      credits: {
        enabled: false,
      },
      yAxis: [
        {
          labels: {
            format: '{value}',
            style: {
              color: 'var(--text-color)',
              fontSize: 9,
            },
          },
          title: {
            text: 'Supply/Return T',
            style: {
              color: '#FFAB00',
              fontSize: 12,
              fontWeight: '500',
            },
          },
          gridLineWidth: 0,
          gridLineColor: '#101D43',
        },
        {
          labels: {
            format: '{value} Pa',
            style: {
              color: 'var(--text-color)',
              fontSize: 9,
            },
          },
          title: {
            text: 'Pressure',
            style: {
              color: '#FFAB00',
              fontSize: 12,
              fontWeight: '500',
            },
          },
          gridLineWidth: 0,
          gridLineColor: '#101D43',
          opposite: true,
        },
        {
          visible: false,
          min: 0,
          max: 5,
        },
      ],
      xAxis: {
        categories: datetime.value || [],
        labels: {
          style: {
            color: 'var(--text-color)',
            fontSize: 10,
            fontWeight: '500',
          },
          step: 1,
          maxStaggerLines: 1,
          rotation: -90,
          align: 'right',
          x: -8,
          y: 25,
          formatter: function() {
            const date = new Date(this.value);
            return date.toLocaleTimeString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true,
            });
          },
        },
        tickPositioner: function() {
          const positions = [];
          const dataLength = this.dataMax - this.dataMin;
          const fixedLabels = 75;

          const tickInterval = dataLength / (fixedLabels - 1);

          for (let i = 0; i < fixedLabels; i++) {
            const position = this.dataMin + (i * tickInterval);
            positions.push(Math.round(position));
          }
          return positions;
        },
        plotBands: (cycleData.value || []).map(band => ({
          ...band,
          color: showCycleRef.value ? band.color : 'transparent',
        })),
      },
      legend: {
        itemStyle: {
          color: 'var(--text-color)',
          fontSize: 10,
        },
      },
      subtitle: {
        text: `Delta T = Return Temp - Supply Temp | ${getDateRangeText()}`,
        style: {
          color: 'var(--text-color)',
          fontSize: 10,
          fontWeight: 'normal',
        },
        align: 'left',
        x: 10,
        y: 460,
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function(event) {
              const comp = this.chart?.vueComponent || window.cycleChartComponent;
              if (comp && typeof comp.handleLegendItemClick === 'function') {
                comp.handleLegendItemClick(this, event, this.chart);
              }
              return false;
            },
          },
          marker: {
            states: {
              select: {
                lineColor: 'rgba(100,100,200, 0.25)',
                fillColor: 'rgba(223, 83, 83, 1)',
                lineWidth: 10,
                radius: 6,
              },
            },
          },
        },
      },
      series: dataSeries.value || [],
      responsive: {
        rules: [{
          condition: {
            maxWidth: '100%',
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        }],
      },
    };
  });

  // Helper functions
  function getRandomBrightColor() {
    const r = Math.floor(Math.random() * 156) + 100;
    const g = Math.floor(Math.random() * 156) + 100;
    const b = Math.floor(Math.random() * 156) + 100;
    return `rgb(${r}, ${g}, ${b})`;
  }

  function formatTooltip(tooltipContext) {
    const { x, points } = tooltipContext;

    if (!points || points.length === 0) return '';

    let tooltipContent = `<b>${x}</b><br/>`;

    // Find Return Temp and Supply Temp for Delta T calculation
    let returnTemp = null;
    let supplyTemp = null;

    // Get all series data at this time point
    points.forEach(point => {
      if (point.y !== null && point.y !== undefined) {
        const value = point.y;
        const seriesName = point.series.name;
        const color = point.series.color;

        // Store values for Delta T calculation
        if (seriesName === 'Return T') {
          returnTemp = value;
        } else if (seriesName === 'Supply T') {
          supplyTemp = value;
        }

        // Format the value based on series type
        let formattedValue;
        if (seriesName === 'Humidity') {
          formattedValue = `${value}%`;
        } else if (seriesName === 'Pressure') {
          formattedValue = `${value} Pa`;
        } else {
          formattedValue = `${value}°F`;
        }

        tooltipContent += `<span style="color: ${color}">●</span> <b>${seriesName}:</b> ${formattedValue}<br/>`;
      }
    });

    // Add Delta T if both temperatures are available
    if (returnTemp !== null && supplyTemp !== null) {
      const deltaT = returnTemp - supplyTemp;
      tooltipContent += `<span style="color: #FF6B35">●</span> <b>Delta T:</b> ${deltaT.toFixed(1)}°F<br/>`;
    }

    return tooltipContent;
  }

  function getDateRangeText() {
    if (!datetime.value || datetime.value.length === 0) return '';

    const startDate = new Date(datetime.value[0]);
    const endDate = new Date(datetime.value[datetime.value.length - 1]);

    const startStr = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const endStr = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    if (startStr === endStr) {
      return startStr;
    }

    return `${startStr} - ${endStr}`;
  }

  function setupChartEventHandlers(chart) {
    const xAxis = chart.xAxis[0];

    // Apply saved legend visibility state immediately
    const componentInstance = window.cycleChartComponent;
    if (componentInstance && componentInstance.applyLegendVisibilityState) {
      componentInstance.applyLegendVisibilityState(chart);
    }

    // Attach click handlers to the chart instance for use by plot bands
    chart.handlePlotBandClick = (plotBand, index) => {
      handlePlotBandClick(plotBand, index, chart, actualCycleData.value, selectedCycleDetails, selectedCycleIndex, updateCycleHoverState);
    };

    chart.handlePlotBandHover = (plotBand, index) => {
      handlePlotBandHover(plotBand, index, chart);
    };

    chart.handlePlotBandMouseOut = (plotBand) => {
      handlePlotBandMouseOut(plotBand, chart);
    };

    // Wait a bit for the chart to fully render, then attach plot band handlers
    setTimeout(() => {
      // Safety check: ensure chart container exists
      if (!chart || !chart.container) {
        console.warn('Chart container not available for plot band handlers');
        return;
      }

      const plotBandPaths = chart.container.querySelectorAll('.highcharts-plot-band');

      xAxis.plotLinesAndBands.forEach((plotBand, index) => {
        const bandElement = plotBandPaths[index];

        if (bandElement) {
          bandElement.addEventListener('click', (event) => {
            chart.handlePlotBandClick(plotBand, index);
          });

          bandElement.addEventListener('mouseover', (event) => {
            chart.handlePlotBandHover(plotBand, index);
          });

          bandElement.addEventListener('mouseout', (event) => {
            chart.handlePlotBandMouseOut(plotBand);
          });
        }
      });
    }, 100);
  }

  function handlePlotBandClick(plotBand, index, chart, actualCycleData, selectedCycleDetails, selectedCycleIndex, updateCycleHoverState) {
    // Get the actual cycle data from the source with safety check
    const actualData = (actualCycleData && actualCycleData[index])
      ? actualCycleData[index]
      : {
          from: plotBand.options.from || plotBand.options.fromValue,
          to: plotBand.options.to || plotBand.options.toValue,
          color: plotBand.options.color,
          id: plotBand.options.id
        };

    // Set cycle details for X-Ray section with proper property access
    const fromValue = plotBand.options.from || plotBand.options.fromValue;
    const toValue = plotBand.options.to || plotBand.options.toValue;
    
    const cycleDetails = {
      plotBandIndex: index,
      cycleIndex: index,
      from: fromValue,
      to: toValue,
      fromFormatted: fromValue ? new Date(fromValue).toLocaleString() : '',
      toFormatted: toValue ? new Date(toValue).toLocaleString() : '',
      duration: (fromValue && toValue) ? Math.round((toValue - fromValue) / 1000 / 60) : 0,
      color: plotBand.options.color,
      id: plotBand.options.id,
      ...actualData
    };

    // Update the selected cycle details and index
    selectedCycleDetails.value = cycleDetails;
    selectedCycleIndex.value = index;
    updateCycleHoverState(index, cycleDetails);
  }

  function handlePlotBandHover(plotBand, index, chart) {
    if (!plotBand.hoverLabel) {
      const xAxis = chart.xAxis[0];
      const middle = (xAxis.toPixels(plotBand.options.from) + xAxis.toPixels(plotBand.options.to)) / 2;

      const fromValue = plotBand.options.from || plotBand.options.fromValue;
      const toValue = plotBand.options.to || plotBand.options.toValue;

      plotBand.hoverLabel = chart.renderer
        .label(
          `${fromValue ? new Date(fromValue).toLocaleString() : 'N/A'} <br> - ${toValue ? new Date(toValue).toLocaleString() : 'N/A'}`,
          middle - 80,
          chart.plotTop - 25,
        )
        .attr({
          zIndex: 5,
          fill: 'rgba(255, 255, 255, 0.9)',
          padding: 8,
        })
        .css({
          fontFamily: 'Poppins, sans-serif !important',
          fontSize: 12,
          fontWeight: 600,
          color: '#1B2138',
        })
        .add();
    }
  }

  function handlePlotBandMouseOut(plotBand, chart) {
    if (plotBand.hoverLabel) {
      plotBand.hoverLabel.destroy();
      plotBand.hoverLabel = null;
    }
  }

  function updateCycleHoverState(cycleIndex, cycleData) {
    cycleHoverState.value = {
      isHovered: true,
      cycleIndex: cycleIndex,
      cycleData: cycleData
    };
  }

  function clearCycleHoverState() {
    cycleHoverState.value = {
      isHovered: false,
      cycleIndex: null,
      cycleData: null
    };
  }

  // Legend management functions
  function handleLegendItemClick(series, event, chart, legendVisibilityState) {
    const seriesName = series.name;
    const currentVisibility = legendVisibilityState[seriesName];

    const newVisibility = currentVisibility === undefined ? false : !currentVisibility;

    const updatedState = { ...legendVisibilityState };
    updatedState[seriesName] = newVisibility;

    series.setVisible(newVisibility, false);
    chart.redraw();

    return {
      seriesName,
      visible: newVisibility,
      visibilityState: updatedState
    };
  }

  function applyLegendVisibilityState(chart, legendVisibilityState) {
    if (!chart || !chart.series) return;

    chart.series.forEach(series => {
      const seriesName = series.name;
      const savedVisibility = legendVisibilityState[seriesName];

      if (savedVisibility !== undefined) {
        series.setVisible(savedVisibility, false);
      }
    });

    chart.redraw();
  }

  function resetLegendVisibility(chart) {
    if (chart && chart.series) {
      chart.series.forEach(series => {
        series.setVisible(true, false);
      });
      chart.redraw();
    }
    return {};
  }

  return {
    // Reactive state
    selectedCycleDetails,
    cycleHoverState,
    selectedCycleIndex,
    hoveredCycleIndex,

    // Computed data
    datetime,
    returnTemp,
    supplyTemp,
    atticTemp,
    outsideTemp,
    humidity,
    filterPressure,
    dataset,
    cycleData,
    actualCycleData,
    dataSeries,
    chartOptions,

    // Methods
    updateCycleHoverState,
    clearCycleHoverState,
    handleLegendItemClick,
    applyLegendVisibilityState,
    resetLegendVisibility,
    getRandomBrightColor
  };
}
